import { useEffect, useState } from "react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import api from "@/lib/api";
import { Card, StatCard, PageTitle } from "@/admin/ui";

const PIE_COLORS = ["#00B8A9", "#9CA3AF", "#5B5E66", "#B7B9BE"];

function TopList({ title, items, testid }) {
  const max = Math.max(...(items || []).map((i) => i.count), 1);
  return (
    <Card className="p-5" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite mb-4">{title}</p>
      {(!items || items.length === 0) && <p className="text-graphite text-sm">No data yet.</p>}
      <div className="space-y-3">
        {(items || []).map((i) => (
          <div key={i.label}>
            <div className="flex justify-between font-mono text-[11px] mb-1">
              <span className="text-chalk/80 truncate mr-3">{i.label}</span>
              <span className="text-graphite">{i.count}</span>
            </div>
            <div className="h-1 bg-border"><div className="h-full bg-signal" style={{ width: `${(i.count / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </Card>
  );
}

const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function SalesSection({ sales }) {
  if (!sales) return null;
  const maxRev = Math.max(...(sales.by_product || []).map((p) => p.revenue), 1);
  return (
    <>
      <Card className="p-5 mt-4" data-testid="sales-by-week">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite mb-4">Revenue by week — last 12 weeks</p>
        {sales.by_week.length === 0 ? <p className="text-graphite text-sm">No paid orders yet.</p> : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sales.by_week}>
                <CartesianGrid stroke="#2A2B2E" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="week" tick={{ fill: "#5B5E66", fontSize: 10, fontFamily: "Space Mono" }} />
                <YAxis tick={{ fill: "#5B5E66", fontSize: 10, fontFamily: "Space Mono" }} width={52} tickFormatter={(v) => `$${v}`} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v, name) => name === "Revenue" ? [fmt$(v), name] : [v, name]}
                  contentStyle={{ background: "#17181B", border: "1px solid #2A2B2E", color: "#EDEEF0", fontFamily: "Space Mono", fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#00B8A9" name="Revenue" maxBarSize={42} />
                <Bar dataKey="orders" fill="#5B5E66" name="Orders" maxBarSize={42} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card className="p-5 mt-4 overflow-x-auto" data-testid="sales-by-product">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite mb-4">Revenue by product — all time</p>
        {sales.by_product.length === 0 ? <p className="text-graphite text-sm">No paid orders yet.</p> : (
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-border font-mono text-[10px] uppercase tracking-wider text-graphite">
                <th className="text-left py-2 pr-4">Product</th>
                <th className="text-right py-2 px-4">Units</th>
                <th className="text-right py-2 px-4">Orders</th>
                <th className="text-right py-2 pl-4">Revenue</th>
                <th className="w-1/3 py-2 pl-6" />
              </tr>
            </thead>
            <tbody>
              {sales.by_product.map((p, i) => (
                <tr key={p.name} className="border-b border-border/40" data-testid={`sales-product-row-${i}`}>
                  <td className="py-2.5 pr-4 text-sm">{p.name}</td>
                  <td className="py-2.5 px-4 text-right font-mono text-xs text-graphite">{p.units}</td>
                  <td className="py-2.5 px-4 text-right font-mono text-xs text-graphite">{p.orders}</td>
                  <td className="py-2.5 pl-4 text-right font-mono text-xs text-signal">{fmt$(p.revenue)}</td>
                  <td className="py-2.5 pl-6">
                    <div className="h-1 bg-border"><div className="h-full bg-signal" style={{ width: `${(p.revenue / maxRev) * 100}%` }} /></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-5 mt-4 overflow-x-auto" data-testid="tax-by-state">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite mb-4">Sales tax collected by state</p>
        {!(sales.by_state || []).length ? <p className="text-graphite text-sm">No paid orders yet.</p> : (
          <table className="w-full min-w-[480px]">
            <thead>
              <tr className="border-b border-border font-mono text-[10px] uppercase tracking-wider text-graphite">
                <th className="text-left py-2 pr-4">State</th>
                <th className="text-right py-2 px-4">Orders</th>
                <th className="text-right py-2 px-4">Revenue</th>
                <th className="text-right py-2 pl-4">Tax collected</th>
              </tr>
            </thead>
            <tbody>
              {sales.by_state.map((r, i) => (
                <tr key={r.state} className="border-b border-border/40" data-testid={`tax-state-row-${i}`}>
                  <td className="py-2.5 pr-4 text-sm">{r.state}</td>
                  <td className="py-2.5 px-4 text-right font-mono text-xs text-graphite">{r.orders}</td>
                  <td className="py-2.5 px-4 text-right font-mono text-xs text-graphite">{fmt$(r.revenue)}</td>
                  <td className="py-2.5 pl-4 text-right font-mono text-xs text-signal">{fmt$(r.tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [sales, setSales] = useState(null);

  useEffect(() => {
    api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  useEffect(() => {
    api.get("/admin/sales").then((r) => setSales(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="font-mono text-xs text-graphite">Loading…</p>;
  const s = data.summary;

  return (
    <div data-testid="admin-analytics-page">
      <PageTitle title="Analytics" sub="Who's moving through the site.">
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`analytics-days-${d}`}
              className={`font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border ${
                days === d ? "bg-chalk text-ink border-chalk" : "border-border text-graphite hover:border-chalk"}`}>
              {d}D
            </button>
          ))}
        </div>
      </PageTitle>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard label="Total Pageviews" value={s.total_pageviews} testid="an-pageviews" />
        <StatCard label="Unique Visitors" value={s.unique_visitors} accent="text-signal" testid="an-unique" />
        <StatCard label="Today" value={s.visitors_today} sub="visitors" testid="an-today" />
        <StatCard label="This Week" value={s.visitors_week} sub="visitors" testid="an-week" />
        <StatCard label="This Month" value={s.visitors_month} sub="visitors" testid="an-month" />
      </div>

      <Card className="p-5 mt-4" data-testid="analytics-chart">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite mb-4">Traffic — last {days} days</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily}>
              <defs>
                <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00B8A9" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#00B8A9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#2A2B2E" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#5B5E66", fontSize: 10, fontFamily: "Space Mono" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fill: "#5B5E66", fontSize: 10, fontFamily: "Space Mono" }} allowDecimals={false} width={32} />
              <Tooltip contentStyle={{ background: "#17181B", border: "1px solid #2A2B2E", color: "#EDEEF0", fontFamily: "Space Mono", fontSize: 12 }} />
              <Area type="monotone" dataKey="pageviews" stroke="#00B8A9" fill="url(#gA)" strokeWidth={2} name="Pageviews" />
              <Area type="monotone" dataKey="visitors" stroke="#9CA3AF" fill="transparent" strokeWidth={2} name="Visitors" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <SalesSection sales={sales} />

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
        <TopList title="Most visited pages" items={data.top_pages} testid="analytics-pages" />
        <TopList title="Traffic sources" items={data.sources} testid="analytics-sources" />
        <Card className="p-5" data-testid="analytics-devices">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite mb-4">Devices</p>
          {data.devices.length === 0 ? <p className="text-graphite text-sm">No data yet.</p> : (
            <>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.devices} dataKey="count" nameKey="label" innerRadius={40} outerRadius={65} strokeWidth={0}>
                      {data.devices.map((d, i) => <Cell key={d.label} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#17181B", border: "1px solid #2A2B2E", color: "#EDEEF0", fontFamily: "Space Mono", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1 mt-2">
                {data.devices.map((d, i) => (
                  <div key={d.label} className="flex justify-between font-mono text-[11px]">
                    <span className="flex items-center gap-2 text-chalk/80">
                      <span className="w-2 h-2 inline-block" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{d.label}
                    </span>
                    <span className="text-graphite">{d.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
        <TopList title="Visitor countries" items={data.countries} testid="analytics-countries" />
      </div>
      <p className="font-mono text-[10px] text-graphite mt-4">
        Country data is provided by the CDN in production and may be unavailable in preview.
      </p>
    </div>
  );
}
